import { Box, useTheme } from '@mui/material';
import {
  useRef,
  useEffect,
  useState,
  useMemo,
} from 'react';

interface LazyRenderContentCategoriesProps {
  categories: (() => JSX.Element)[];
  isDesktop: boolean;
  batchSize: number;
}

export const LazyRenderContentCategories = ({
  categories,
  isDesktop,
  batchSize,
}:LazyRenderContentCategoriesProps) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const theme = useTheme();
  // reference for dummy element that is used as a trigger point for the scroll observer.
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isDesktop || !sentinelRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {}; // Return a no-op cleanup function
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + batchSize, categories.length));
        }
      },
      {
        root: null,
        // If the sentinel is within 200px of entering the viewport, trigger the callback.
        rootMargin: theme.spacing(25),
        threshold: 0.1,
      },
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchSize, isDesktop, categories.length]);

  const itemsToRender = useMemo(() => (isDesktop ? categories :
    categories.slice(0, visibleCount)), [categories, visibleCount, isDesktop]);

  return (
    <>
      {itemsToRender.map((ItemToRender, index) => <ItemToRender key={index}/>)}
      {!isDesktop && visibleCount < categories.length &&
      <Box ref={sentinelRef} />}
    </>
  );
};
